<script lang="ts">
  import axios from 'axios';
  import { onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { clic } from '../directives/actions.svelte';
  import Bouton from '../ui/Bouton.svelte';
  import ChampTexte from '../ui/ChampTexte.svelte';
  import Formulaire from '../ui/Formulaire.svelte';
  import Modale from '../ui/Modale.svelte';
  import ZoneTexte from '../ui/ZoneTexte.svelte';
  import { entrepotNavigateurAvisUtilisateur } from './ControleAvisUtilisateur';

  const ROUTES_À_EXCLURE: RegExp[] = [/^\/cyberdepart\/diagnostic-gratuit/, /^\/conseils-cyber/];

  let encartOuvert = $state(false);
  let afficheDialogue = $state(false);
  let étape: 'formulaire' | 'merci' = $state('formulaire');
  const titreDialogue = $derived(
    étape === 'formulaire'
      ? 'Votre avis nous intéresse\u00a0!'
      : 'Merci 🤩\u00a0! Vos remarques sont précieuses pour faire évoluer le service.'
  );

  type SatisfactionDisponible = 1 | 2 | 3 | 4 | 5;
  const niveauxSatisfaction: {
    id: string;
    valeur: SatisfactionDisponible;
    libellé: string;
    emoji: string;
  }[] = [
    { id: 'pas-du-tout', valeur: 1, libellé: 'Pas du tout', emoji: '😠' },
    { id: 'pas-satisfait', valeur: 2, libellé: 'Pas satisfait', emoji: '☹️' },
    { id: 'moyennement-satisfait', valeur: 3, libellé: 'Moyennement satisfait', emoji: '😕' },
    { id: 'satisfait', valeur: 4, libellé: 'Satisfait', emoji: '😊' },
    { id: 'tout-a-fait', valeur: 5, libellé: 'Tout à fait', emoji: '🤩' },
  ];

  let satisfaction: SatisfactionDisponible | undefined = $state();
  let erreurSatisfaction = $state(false);
  let commentaire = $state('');
  let erreurCommentaire = $state(false);
  let emailDeContact: string | undefined = $state();
  const pageSource = sessionStorage.getItem('pageSource');

  $effect(() => {
    if (satisfaction) erreurSatisfaction = false;
    if (commentaire) erreurCommentaire = false;
  });

  const surCliqueCTA = () => {
    encartOuvert = false;
    afficheDialogue = true;
  };

  const surFermetureCTA = () => {
    encartOuvert = false;
    entrepotNavigateurAvisUtilisateur.modifieDateDerniereFermetureAvis(new Date());
  };

  const enregistreFermetureDialogue = () => {
    entrepotNavigateurAvisUtilisateur.modifieDateDerniereFermetureAvis(new Date());
  };

  const fermeDialogue = () => {
    afficheDialogue = false;
    enregistreFermetureDialogue();
  };

  const soumetsLeFormulaire = async () => {
    erreurSatisfaction = !satisfaction;
    erreurCommentaire = !commentaire;
    if (erreurCommentaire || erreurSatisfaction) return;

    try {
      await axios.post(`/api/avis-utilisateur?pageSource=${pageSource}`, {
        niveauDeSatisfaction: satisfaction,
        commentaire,
        emailDeContact,
      });
    } catch (erreur) {
      console.error(erreur);
    } finally {
      entrepotNavigateurAvisUtilisateur.modifieDateDernierAvisDonne(new Date());
      étape = 'merci';
    }
  };

  onMount(() => {
    encartOuvert = !ROUTES_À_EXCLURE.some((route) => route.exec(window.location.pathname));
  });
</script>

{#if encartOuvert}
  <div class="avis-utilisateur-cta" transition:fly={{ duration: 1000, x: 140, opacity: 1 }}>
    <button class="zone-cliquable" use:clic={surCliqueCTA} tabindex={null}>
      <div class="illustration">
        <img src="/assets/images/dragon-coeur.svg" width="98" height="113" alt="Dragon coeur" />
      </div>
      <p class="texte">Votre avis<br /> nous intéresse&nbsp;!</p>
    </button>
    <button use:clic={surFermetureCTA} tabindex={null} aria-label="Fermer" class="fermer">
      <lab-anssi-icone nom="close-line" taille="sm"></lab-anssi-icone>
    </button>
  </div>
{/if}
<Modale bind:estOuverte={afficheDialogue} titre={titreDialogue} surFermeture={enregistreFermetureDialogue}>
  <div class="dialogue-avis-utilisateur">
    {#if étape === 'formulaire'}
      <Formulaire id="formulaire-avis-utilisateur" surFormulaireValide={soumetsLeFormulaire}>
        <div class="contenu">
          {#if erreurSatisfaction || erreurCommentaire}
            <lab-anssi-alerte
              type="erreur"
              description="Merci de compléter les champs avant d’envoyer votre avis."
              fermable={false}
            ></lab-anssi-alerte>
          {/if}
          <div class="question">
            <p class:erreur={erreurSatisfaction}>Le service MesServicesCyber répond-il à vos attentes&nbsp;?</p>
            <div class="satisfaction">
              <div class="niveaux-satisfaction">
                {#each niveauxSatisfaction as niveau (niveau.valeur)}
                  <input id={niveau.id} type="radio" name="note" value={niveau.valeur} bind:group={satisfaction} />
                  <label class="niveau-satisfaction" for={niveau.id}>
                    <span aria-label={niveau.libellé} role="img">{niveau.emoji}</span>
                  </label>
                {/each}
              </div>
              <div class="descriptions">
                <span class="premier">Pas du tout</span>
                <span class="dernier">Tout à fait</span>
              </div>
            </div>
          </div>
          <div class="question">
            <p class:erreur={erreurCommentaire}>Que pouvons-nous améliorer ?</p>
            <ZoneTexte bind:valeur={commentaire} enErreur={erreurCommentaire} />
          </div>
          <p class="mis-en-avant">
            Échangez avec nous sur votre expérience et participez aux futures évolutions de la plateforme&nbsp;!
          </p>
          <div class="question">
            <ChampTexte
              aideSaisie="Ex : jean.dupont@mail.com"
              id="email-contact"
              libelle="Email de contact (facultatif)"
              messageErreur="L'email est invalide"
              nom="email"
              type="email"
              bind:valeur={emailDeContact}
            />
            <p class="information">
              Votre email ne sera utilisé que pour échanger sur la plateforme et ses futures évolutions.
            </p>
          </div>
        </div>
      </Formulaire>
    {:else}
      <div class="contenu">
        <p>
          Si vous avez renseigné votre adresse email, nous vous recontacterons très prochainement pour échanger sur la
          plateforme et ses futures évolutions.
        </p>
      </div>
    {/if}
  </div>
  {#snippet actions()}
    {#if étape === 'formulaire'}
      <Bouton
        etire
        libelle="Envoyer"
        type="primaire"
        taille="md"
        boutonSoumission={true}
        idFormulaire="formulaire-avis-utilisateur"
      />
      <Bouton etire type="secondaire" libelle="Fermer sans répondre" surClic={fermeDialogue} />
    {:else}
      <Bouton etire libelle="Terminer" type="tertiaire-sans-bordure" taille="md" surClic={fermeDialogue} />
    {/if}
  {/snippet}
</Modale>

<style lang="scss">
  .avis-utilisateur-cta {
    background-color: var(--background-default-grey);
    border-radius: 8px 0 0 8px;
    box-shadow: 0 6px 18px 0 rgba(0, 0, 18, 0.16);
    display: flex;
    flex-direction: column;
    position: fixed;
    right: 0;
    top: 208px;
    width: 124px;
    z-index: calc(var(--ground) + 760);

    .fermer {
      cursor: pointer;
      background: transparent;
      border: 1px solid transparent;
      position: absolute;
      top: 0;
      right: 0;
      padding: 8px;
      margin: 2px 4px;

      &:focus-visible {
        outline: 2px solid var(--bleu-contour-mis-en-valeur);
        outline-offset: 2px;
      }

      &:hover {
        border-color: rgba(0, 0, 0, 0.1);
      }
      &:active {
        border-color: rgba(0, 0, 0, 0.2);
      }
    }

    .zone-cliquable {
      cursor: pointer;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 0.5rem 0 0 0.5rem;
      overflow: hidden;

      &:focus-visible {
        outline: 2px solid var(--bleu-contour-mis-en-valeur);
        outline-offset: 2px;
      }

      &:hover {
        background-color: var(--controle-segmente-fond-hover);
      }

      &:active {
        background-color: var(--controle-segmente-fond-clique);
      }

      .illustration {
        background-color: var(--green-bourgeon-975-75);
        padding-block-start: 0.438rem;

        img {
          display: block;
          margin-inline: auto;
        }
      }

      .texte {
        color: var(--text-label-grey);
        font-size: 0.875rem;
        font-weight: 700;
        line-height: 1.25rem;
        margin: 8px 8px 12px;
        text-align: center;
      }
    }
  }

  .dialogue-avis-utilisateur {
    .contenu {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .mis-en-avant {
        font-weight: bold;
        margin: 8px 0 0;
      }

      .question {
        display: flex;
        flex-direction: column;
        gap: 8px;

        p {
          margin: 0;

          &.erreur {
            color: var(--text-default-error);
          }
        }

        .information {
          color: var(--text-mention-grey);
          font-size: 0.75rem;
          line-height: 1.25rem;
          margin-top: 2px;
        }

        :global(textarea) {
          resize: vertical;
        }
      }
    }

    .satisfaction {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .niveaux-satisfaction {
        display: flex;
        flex-direction: row;

        input[type='radio'] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;

          + label > span {
            cursor: pointer;

            &:hover {
              background-color: var(--gris-secondaire-fond-hover);
            }

            &:active {
              background-color: var(--gris-secondaire-fond-clique);
            }
          }

          &:focus-visible + label {
            outline: 2px solid var(--bleu-contour-mis-en-valeur);
            outline-offset: 2px;
          }

          &:checked + label > span {
            background-color: var(--background-active-blue-france);
            &:hover {
              background-color: var(--blue-france-925-125-hover);
            }
            &:active {
              background-color: var(--blue-france-925-125-active);
            }
          }
        }

        .niveau-satisfaction {
          display: flex;
          flex: 1;
          border: 1px solid var(--border-default-grey);
          border-right: 0;
          border-radius: 0;

          &[for='pas-du-tout'] {
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
          }

          &[for='tout-a-fait'] {
            border-right: 1px solid var(--border-default-grey);
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
          }

          span {
            flex: 1;
            font-size: 1.25rem;
            line-height: 1.5rem;
            margin: 0;
            padding: 8px 16px;
            text-align: center;
          }
        }
      }

      .descriptions {
        color: var(--text-mention-grey);
        display: flex;
        flex-direction: row;
        font-size: 0.875rem;
        font-style: normal;
        justify-content: space-between;
        line-height: 1.5rem;
      }
    }
  }
</style>
